import React from 'react';
import { AlertTriangle, RefreshCw } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';

/**
 * ErrorBoundary - Catches JavaScript errors in child components
 * 
 * Prevents the entire app from crashing when a component throws an error.
 * Shows a user-friendly error message with option to retry.
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({ errorInfo });
    
    // Log error to console in development
    if (process.env.NODE_ENV === 'development') {
      console.error('ErrorBoundary caught an error:', error, errorInfo);
    }
    
    // TODO: Send error to logging service in production
    // logErrorToService(error, errorInfo);
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
  };

  render() {
    if (this.state.hasError) {
      // Custom fallback UI if provided
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default error UI
      return (
        <div className="min-h-[400px] flex items-center justify-center p-4">
          <Card className="max-w-md w-full">
            <CardContent className="pt-6">
              <div className="text-center">
                <AlertTriangle className="w-12 h-12 text-amber-500 mx-auto mb-4" />
                <h2 className="text-lg font-semibold text-slate-900 dark:text-white mb-2">
                  Something went wrong
                </h2>
                <p className="text-sm text-slate-500 dark:text-slate-400 mb-4">
                  {this.props.message || 'An unexpected error occurred. Please try again.'}
                </p>
                
                {process.env.NODE_ENV === 'development' && this.state.error && (
                  <details className="text-left text-xs bg-slate-100 dark:bg-slate-800 p-3 rounded mb-4 max-h-32 overflow-auto">
                    <summary className="cursor-pointer text-slate-600 dark:text-slate-300 mb-2">
                      Error Details
                    </summary>
                    <pre className="text-red-600 dark:text-red-400 whitespace-pre-wrap">
                      {this.state.error.toString()}
                    </pre>
                  </details>
                )}
                
                <div className="flex gap-2 justify-center">
                  <Button 
                    variant="outline" 
                    onClick={() => window.location.reload()}
                    className="gap-2"
                  >
                    <RefreshCw className="w-4 h-4" />
                    Reload Page
                  </Button>
                  {this.props.onRetry && (
                    <Button 
                      onClick={() => {
                        this.handleRetry();
                        this.props.onRetry?.();
                      }}
                      className="bg-teal-600 hover:bg-teal-700"
                    >
                      Try Again
                    </Button>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
